import React, { useState, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SourceMetrics = ({ data, impressionsData }) => {
  const [dateIndex, setDateIndex] = useState(-1); // -1 means "All Dates"
  const [viewType, setViewType] = useState("absolute"); // 'absolute' or 'percentage'
  const [chartType, setChartType] = useState("bar"); // 'bar' or 'line'
  const [dataType, setDataType] = useState("clicks"); // 'clicks' or 'impressions'

  // Use the selected data type
  const activeData =
    dataType === "impressions" && impressionsData ? impressionsData : data;

  // Format date for display
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split("-");
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  const availableDates = useMemo(() => {
    if (!activeData || activeData.length === 0) return [];
    return [...new Set(activeData.map((item) => item.date))].sort(
      (a, b) => new Date(a) - new Date(b)
    );
  }, [activeData]);

  const selectedDate = dateIndex >= 0 ? availableDates[dateIndex] : null;

  const sourceTypes = useMemo(() => {
    if (!activeData || activeData.length === 0) return [];

    // Get all unique source types across all dates
    const sources = new Set();
    activeData.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (
          key !== "date" &&
          item[key] !== null &&
          item[key] !== 0 &&
          item[key] !== "0"
        ) {
          sources.add(key);
        }
      });
    });

    return Array.from(sources);
  }, [activeData]);

  // Process data for the chart
  const chartData = useMemo(() => {
    if (!activeData || activeData.length === 0 || sourceTypes.length === 0)
      return null;

    let processedData;

    if (selectedDate) {
      // Single date mode
      const dateData = activeData.find((item) => item.date === selectedDate);
      if (!dateData) return null;

      const sources = sourceTypes
        .map((source) => ({
          source,
          value: Number(dateData[source] || 0),
        }))
        .filter((item) => item.value > 0);

      // Sort by value in descending order
      sources.sort((a, b) => b.value - a.value);

      const total = sources.reduce((sum, item) => sum + item.value, 0);

      processedData = {
        date: selectedDate,
        formattedDate: formatDate(selectedDate),
        sources,
        total,
      };
    } else {
      // All dates mode (aggregate)
      const sourceTotals = {};
      let total = 0;

      // Sum up values for each source across all dates
      activeData.forEach((dateData) => {
        sourceTypes.forEach((source) => {
          const value = Number(dateData[source] || 0);
          if (value > 0) {
            sourceTotals[source] = (sourceTotals[source] || 0) + value;
            total += value;
          }
        });
      });

      const sources = Object.entries(sourceTotals)
        .map(([source, value]) => ({ source, value }))
        .filter((item) => item.value > 0)
        .sort((a, b) => b.value - a.value);

      processedData = {
        date: "all",
        formattedDate: "All Dates",
        sources,
        total,
      };
    }

    // Prepare chart data
    const barColors = [
      "rgba(54, 162, 235, 0.7)", // Blue
      "rgba(255, 99, 132, 0.7)", // Red
      "rgba(255, 205, 86, 0.7)", // Yellow
      "rgba(75, 192, 192, 0.7)", // Green
      "rgba(153, 102, 255, 0.7)", // Purple
      "rgba(255, 159, 64, 0.7)", // Orange
      "rgba(201, 203, 207, 0.7)", // Grey
      "rgba(54, 162, 235, 0.5)", // Light Blue
      "rgba(255, 99, 132, 0.5)", // Light Red
      "rgba(75, 192, 192, 0.5)", // Light Green
    ];

    // Prepare data for standard bar chart
    const standardChartData = {
      labels: processedData.sources.map((item) => item.source),
      datasets: [
        {
          label: viewType === "percentage" ? "Percentage" : "Count",
          data: processedData.sources.map((item) =>
            viewType === "percentage"
              ? ((item.value / processedData.total) * 100).toFixed(1)
              : item.value
          ),
          backgroundColor: barColors.slice(0, processedData.sources.length),
          borderColor: "rgba(255, 255, 255, 0.3)",
          borderWidth: 1,
        },
      ],
    };

    // Prepare data for line chart (time trend) - show all sources
    const allSources = [...processedData.sources]
      .sort((a, b) => b.value - a.value)
      .map((item) => item.source);

    // Then, prepare line data for each of these sources across all dates
    const lineChartData = {
      labels: availableDates.map((date) => formatDate(date)),
      datasets: allSources.map((source, index) => {
        const sourceData = availableDates.map((date) => {
          const dateData = activeData.find((item) => item.date === date);
          if (!dateData) return 0;

          // If we're in percentage mode, we need to calculate the total for each date
          if (viewType === "percentage") {
            const dateTotal = sourceTypes.reduce((sum, sourceType) => {
              return sum + Number(dateData[sourceType] || 0);
            }, 0);

            return dateTotal > 0
              ? ((Number(dateData[source] || 0) / dateTotal) * 100).toFixed(1)
              : 0;
          }

          return Number(dateData[source] || 0);
        });

        return {
          label: source,
          data: sourceData,
          borderColor: barColors[index % barColors.length].replace("0.7", "1"),
          backgroundColor: barColors[index % barColors.length].replace(
            "0.7",
            "0.1"
          ),
          tension: 0.1,
          pointRadius: 3,
          borderWidth: 2,
        };
      }),
    };

    return {
      ...processedData,
      standardChartData,
      lineChartData,
    };
  }, [activeData, selectedDate, sourceTypes, availableDates, viewType]);

  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-8 text-center">
        <div className="text-gray-400">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
            />
          </svg>
          <h3 className="text-lg font-medium">No Source Data Available</h3>
          <p className="mt-2">
            Please upload source distribution data to view this chart.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-200">
            Traffic Source Analysis
          </h2>
          <p className="text-sm text-gray-400">
            Understanding where your audience comes from
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setChartType("bar")}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              chartType === "bar"
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            Distribution
          </button>
          <button
            onClick={() => setChartType("line")}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              chartType === "line"
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            Trends
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mb-6">
        {chartType === "bar" && (
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Date Range
            </label>
            <select
              value={dateIndex}
              onChange={(e) => setDateIndex(Number(e.target.value))}
              className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={-1}>All Dates (Aggregate)</option>
              {availableDates.map((date, index) => (
                <option key={date} value={index}>
                  {formatDate(date)}
                </option>
              ))}
            </select>
          </div>
        )}

        {impressionsData && (
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Data Type
            </label>
            <select
              value={dataType}
              onChange={(e) => setDataType(e.target.value)}
              className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="clicks">Clicks</option>
              <option value="impressions">Impressions</option>
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1">
            View Type
          </label>
          <select
            value={viewType}
            onChange={(e) => setViewType(e.target.value)}
            className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="absolute">Absolute Values</option>
            <option value="percentage">Percentage</option>
          </select>
        </div>
      </div>

      {chartData && (
        <div className="bg-gray-800 p-6 rounded-lg">
          {chartType === "bar" ? (
            <>
              <div className="mb-4 text-center">
                <h3 className="text-lg font-medium text-gray-200">
                  {dataType === "impressions" ? "Impressions" : "Clicks"} by
                  Source {chartData.formattedDate}
                </h3>
                <div className="text-sm text-gray-400 flex justify-center gap-3">
                  <span>
                    Total:{" "}
                    <span className="font-medium text-gray-300">
                      {chartData.total.toLocaleString()}
                    </span>
                  </span>
                </div>
              </div>

              <div className="h-96">
                <Bar
                  data={chartData.standardChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: "y",
                    plugins: {
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            const value = context.raw;
                            const datasetLabel = context.dataset.label || "";

                            if (viewType === "percentage") {
                              return `${datasetLabel}: ${value}%`;
                            }
                            return `${datasetLabel}: ${value.toLocaleString()}`;
                          },
                        },
                        backgroundColor: "#374151",
                        titleColor: "#E5E7EB",
                        bodyColor: "#E5E7EB",
                        padding: 12,
                        cornerRadius: 4,
                      },
                    },
                    scales: {
                      x: {
                        grid: {
                          color: "rgba(255, 255, 255, 0.1)",
                        },
                        ticks: {
                          color: "#E5E7EB",
                          callback: function (value) {
                            if (viewType === "percentage") {
                              return value + "%";
                            }
                            return value >= 1000
                              ? (value / 1000).toFixed(1) + "k"
                              : value;
                          },
                        },
                      },
                      y: {
                        grid: {
                          color: "rgba(255, 255, 255, 0.1)",
                        },
                        ticks: {
                          color: "#E5E7EB",
                        },
                      },
                    },
                    animation: {
                      duration: 300,
                    },
                  }}
                />
              </div>

              <div className="mt-8 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-700">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                      >
                        Source
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider"
                      >
                        Count
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider"
                      >
                        Percentage
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-800 divide-y divide-gray-700">
                    {chartData.sources.map((item, index) => (
                      <tr
                        key={item.source}
                        className={
                          index % 2 === 0 ? "bg-gray-800" : "bg-gray-750"
                        }
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {item.source}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-right">
                          {item.value.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-right">
                          {((item.value / chartData.total) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4 text-center">
                <h3 className="text-lg font-medium text-gray-200">
                  Source Trends Over Time
                </h3>
                <p className="text-sm text-gray-400">
                  {viewType === "percentage" ? "Percentage" : "Count"} of{" "}
                  {dataType === "impressions" ? "impressions" : "clicks"} over
                  time by source
                </p>
              </div>

              <div className="h-96">
                <Line
                  data={chartData.lineChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: "index",
                      intersect: false,
                    },
                    plugins: {
                      legend: {
                        position: "top",
                        labels: {
                          color: "#E5E7EB",
                          font: {
                            size: 12,
                          },
                          usePointStyle: true,
                          padding: 15,
                        },
                      },
                      tooltip: {
                        backgroundColor: "#374151",
                        titleColor: "#E5E7EB",
                        bodyColor: "#E5E7EB",
                        padding: 12,
                        cornerRadius: 4,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: "rgba(255, 255, 255, 0.1)",
                        },
                        ticks: {
                          color: "#E5E7EB",
                          callback: function (value) {
                            if (viewType === "percentage") {
                              return value + "%";
                            }
                            return value >= 1000
                              ? (value / 1000).toFixed(1) + "k"
                              : value;
                          },
                        },
                      },
                      x: {
                        grid: {
                          color: "rgba(255, 255, 255, 0.1)",
                        },
                        ticks: {
                          color: "#E5E7EB",
                        },
                      },
                    },
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SourceMetrics;
